/**
 * Integration Layer - Main Entry Point
 *
 * This module provides the main entry point for the integration layer that
 * connects the unified request coordinator system with Axios and other
 * external systems. It exports all integration components and provides
 * convenient factory functions.
 */

// =============================================================================
// MAIN INTEGRATION EXPORTS
// =============================================================================

export {
  AxiosIntegration,
  createAxiosIntegration,
  setupInterceptors,
  preserveExistingBehavior,
  isIntegrationSetup,
  getIntegrationContext,
  createIntegrationConfig,
} from './axios.integration';

// =============================================================================
// AUTHENTICATION INTEGRATION EXPORTS
// =============================================================================

export {
  AuthIntegration,
  createAuthIntegration,
  classifyAuthError,
  isAuthError,
  AuthError,
} from './auth.integration';

// =============================================================================
// PERFORMANCE INTEGRATION EXPORTS
// =============================================================================

export {
  PerformanceIntegration,
  createPerformanceIntegration,
  createPerformanceHooks,
  calculateNetworkMetrics,
  formatMetrics,
  exportMetrics,
} from './performance.integration';

// =============================================================================
// UNIFIED INTEGRATION FACTORY
// =============================================================================

import { AuthIntegration } from './auth.integration';
import { AxiosIntegration } from './axios.integration';
import { PerformanceIntegration } from './performance.integration';

import type { IAxiosIntegration } from '../../../types/api';
import type {
  UnifiedIntegrationConfig,
  UnifiedIntegration,
} from '../../../types/api/integration.types';
import type { AxiosInstance } from 'axios';

/**
 * Create a unified integration that combines all integration components
 */
export function createUnifiedIntegration(
  client: AxiosInstance,
  config: UnifiedIntegrationConfig = {}
): UnifiedIntegration {
  // Create AuthIntegration first so it can be passed to AxiosIntegration
  const authIntegration = new AuthIntegration(config.auth);
  const axiosIntegration = new AxiosIntegration(
    client,
    config.axios,
    authIntegration
  );
  const performanceIntegration = new PerformanceIntegration(config.performance);

  return {
    axios: axiosIntegration,
    auth: authIntegration,
    performance: performanceIntegration,

    setup(): void {
      axiosIntegration.setupInterceptors();
      console.log('[UnifiedIntegration] Setup complete');
    },

    cleanup(): void {
      axiosIntegration.cleanup();
      performanceIntegration.destroy();
      console.log('[UnifiedIntegration] Cleanup complete');
    },

    getMetrics(): {
      axios: ReturnType<IAxiosIntegration['getMetrics']>;
      performance: ReturnType<PerformanceIntegration['getPerformanceSnapshot']>;
    } {
      return {
        axios: axiosIntegration.getMetrics(),
        performance: performanceIntegration.getPerformanceSnapshot(),
      };
    },
  };
}

// =============================================================================
// CONVENIENCE FUNCTIONS
// =============================================================================

/**
 * Quick setup function that creates and configures all integrations
 */
export function setupIntegrationLayer(
  client: AxiosInstance,
  config: UnifiedIntegrationConfig = {}
): UnifiedIntegration {
  const integration = createUnifiedIntegration(client, config);
  integration.setup();
  return integration;
}

/**
 * Create integration configuration with sensible defaults for production
 */
export function createProductionIntegrationConfig(): UnifiedIntegrationConfig {
  return {
    axios: {
      enableLegacyMode: false,
      debug: false,
      retry: {
        maxAttempts: 3,
        baseDelay: 1000,
        maxDelay: 10000,
        exponentialBase: 2,
        jitterFactor: 0.1,
        enableCircuitBreaker: true,
        retryableStatusCodes: [408, 429, 500, 502, 503, 504],
        retryableMethods: ['GET', 'PUT', 'HEAD', 'OPTIONS', 'DELETE'],
        excludeEndpoints: ['/login', '/logout', '/register', '/api/user/auth'],
      },
      deduplication: {
        enabled: true,
        ttl: 60000,
        maxCacheSize: 1000,
      },
      enablePerformanceMonitoring: true,
      enableNotifications: true,
      enableLogging: false,
    },
    auth: {
      enableAutoRefresh: true,
    },
    performance: {
      enableDetailedMetrics: false,
      enableRetryTracking: true,
      enableCircuitBreakerMetrics: true,
      memoryEfficientStorage: true,
      maxStoredRequests: 500,
      metricsRetentionMs: 5 * 60 * 1000, // 5 minutes
    },
  };
}

/**
 * Create integration configuration for tests that don't need retry behavior.
 *
 * This config disables retries, deduplication, auto-refresh, and notifications
 * to ensure tests run fast and predictably without timeouts from retry delays.
 *
 * Use this for unit tests that:
 * - Test error handling (401, 500, etc.) without triggering retries
 * - Need synchronous, predictable request behavior
 * - Don't require testing retry/deduplication/auth-refresh flows
 *
 * @example
 * ```typescript
 * const client = createApiClient(createTestIntegrationConfig());
 * // Mock a 500 error - test completes immediately without retry delays
 * ```
 */
export function createTestIntegrationConfig(): UnifiedIntegrationConfig {
  return {
    axios: {
      enableLegacyMode: false,
      debug: false,
      retry: {
        maxAttempts: 1,
        baseDelay: 0,
        maxDelay: 0,
        exponentialBase: 1,
        jitterFactor: 0,
        enableCircuitBreaker: false,
        retryableStatusCodes: [], // Empty array = no retries
        retryableMethods: [],
        excludeEndpoints: [],
      },
      deduplication: {
        enabled: false,
        ttl: 0,
        maxCacheSize: 0,
      },
      enablePerformanceMonitoring: false,
      enableNotifications: false,
      enableLogging: false,
    },
    auth: {
      enableAutoRefresh: false,
    },
    performance: {
      enableDetailedMetrics: false,
      enableRetryTracking: false,
      enableCircuitBreakerMetrics: false,
      memoryEfficientStorage: true,
      maxStoredRequests: 10,
      metricsRetentionMs: 1000,
    },
  };
}

/**
 * Create integration configuration for testing retry/dedup/auth flows.
 *
 * This config enables all features but with fast delays suitable for
 * use with vi.useFakeTimers(). Tests can advance time predictably.
 *
 * Use this for integration tests that:
 * - Test retry behavior with vi.advanceTimersByTimeAsync()
 * - Test deduplication flows
 * - Test auth refresh flows
 *
 * IMPORTANT: Tests using this config MUST call vi.useFakeTimers() in beforeEach
 * and vi.useRealTimers() in afterEach.
 *
 * @example
 * ```typescript
 * beforeEach(() => { vi.useFakeTimers(); });
 * afterEach(() => { vi.useRealTimers(); });
 *
 * it('retries on 503', async () => {
 *   const client = createApiClient(createFastTestIntegrationConfig());
 *   // Mock 503 twice, then 200
 *   const promise = client.get('/endpoint');
 *   await vi.advanceTimersByTimeAsync(10); // First retry at 10ms
 *   await vi.advanceTimersByTimeAsync(20); // Second retry at 20ms
 *   await expect(promise).resolves.toMatchObject({ status: 200 });
 * });
 * ```
 */
export function createFastTestIntegrationConfig(): UnifiedIntegrationConfig {
  return {
    axios: {
      enableLegacyMode: false,
      debug: false,
      retry: {
        maxAttempts: 3,
        baseDelay: 10, // Fast: 10ms base delay
        maxDelay: 50, // Fast: 50ms max delay
        exponentialBase: 2,
        jitterFactor: 0, // No jitter for predictable timing
        enableCircuitBreaker: true,
        retryableStatusCodes: [408, 429, 500, 502, 503, 504],
        retryableMethods: ['GET', 'PUT', 'HEAD', 'OPTIONS', 'DELETE'],
        excludeEndpoints: ['/login', '/logout', '/register', '/api/user/auth'],
      },
      deduplication: {
        enabled: false, // Disabled to avoid setInterval conflicts with fake timers
        ttl: 0,
        maxCacheSize: 0,
      },
      enablePerformanceMonitoring: true,
      enableNotifications: true,
      enableLogging: false,
    },
    auth: {
      enableAutoRefresh: true,
    },
    performance: {
      enableDetailedMetrics: true,
      enableRetryTracking: true,
      enableCircuitBreakerMetrics: true,
      memoryEfficientStorage: true,
      maxStoredRequests: 100,
      metricsRetentionMs: 5000,
    },
  };
}

/**
 * Create integration configuration with debugging enabled for development
 */
export function createDevelopmentIntegrationConfig(): UnifiedIntegrationConfig {
  return {
    axios: {
      enableLegacyMode: false,
      debug: true,
      retry: {
        maxAttempts: 5,
        baseDelay: 500,
        maxDelay: 5000,
        exponentialBase: 2,
        jitterFactor: 0.1,
        enableCircuitBreaker: true,
        retryableStatusCodes: [408, 429, 500, 502, 503, 504],
        retryableMethods: ['GET', 'PUT', 'HEAD', 'OPTIONS', 'DELETE'],
        excludeEndpoints: ['/login', '/logout', '/register', '/api/user/auth'],
      },
      deduplication: {
        enabled: true,
        ttl: 30000,
        maxCacheSize: 100,
        enableLogging: true,
      },
      enablePerformanceMonitoring: true,
      enableNotifications: true,
      enableLogging: true,
    },
    auth: {
      enableAutoRefresh: true,
    },
    performance: {
      enableDetailedMetrics: true,
      enableRetryTracking: true,
      enableCircuitBreakerMetrics: true,
      enableTimelineReconstruction: true,
      memoryEfficientStorage: false,
      maxStoredRequests: 1000,
      metricsRetentionMs: 10 * 60 * 1000, // 10 minutes
    },
  };
}

// =============================================================================
// TYPE EXPORTS
// =============================================================================

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

export default {
  createUnifiedIntegration,
  setupIntegrationLayer,
  createProductionIntegrationConfig,
  createDevelopmentIntegrationConfig,
  createTestIntegrationConfig,
  createFastTestIntegrationConfig,
};
